import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import TextField from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
import MenuItem from '@material-ui/core/MenuItem';
import {withStyles} from '@material-ui/core/styles';

function renderInputComponent(inputProps) {
    const {
        classes, inputRef = () => {
        }, ref, ...other
    } = inputProps;

    return (
        <TextField
            fullWidth
            InputProps={{
                inputRef: node => {
                    ref(node);
                    inputRef(node);
                },
                classes: {
                    input: classes.input,
                },
            }}
            {...other}
        />
    );
}

function renderSuggestion(suggestion, {query, isHighlighted}) {
    const matches = match(suggestion.label, query);
    const parts = parse(suggestion.label, matches);

    return (
        <MenuItem selected={isHighlighted} component="div">
            <div>
                {parts.map((part, index) => {
                    return part.highlight ? (
                        <span key={String(index)} style={{fontWeight: 500}}>
              {part.text}
            </span>
                    ) : (
                        <strong key={String(index)} style={{fontWeight: 300}}>
                            {part.text}
                        </strong>
                    );
                })}
            </div>
        </MenuItem>
    );
}

function getSuggestionValue(suggestion) {
    return suggestion.label;
}

const styles = theme => ({
    root: {
        flexGrow: 1,
    },
    container: {
        position: 'relative',
    },
    suggestionsContainerOpen: {
        position: 'absolute',
        zIndex: 1000,
        marginTop: theme.spacing(1),
        left: 0,
        right: 0,
    },
    suggestion: {
        display: 'block',
    },
    suggestionsList: {
        margin: 0,
        padding: 0,
        listStyleType: 'none',
    },
    divider: {
        height: theme.spacing(2),
    },
});

class GenericAutosuggest extends PureComponent {
    constructor(props) {
        super(props);

        this.areSuggestionsShown = false;

        this.state = {
            value: this.props.value || '',
            popper: '',
            suggestions: [],
        };
    }

    componentWillReceiveProps(nextProps, nextContext) {
        if (this.state.value !== nextProps.value) {
            this.setState({
                value: nextProps.value || '',
                popper: '',
                suggestions: []
            });
        }
    }

    handleSuggestionsFetchRequested = async ({value}) => {
        if (this.fetchAutocompleteTimeoutId)
            clearTimeout(this.fetchAutocompleteTimeoutId);

        this.fetchAutocompleteTimeoutId = setTimeout(async () => {
            // If while waiting for timeout, the address has changed, dont proceed
            if (this.state.value !== value)
                return;

            const predictions = await this.props.fetchPredictions(value);

            // If somehow, the current suggestions are for the current address, dont change the suggestions
            // It can happen if by some reason, the autocomplete query delayed and another, after, autocomplete query was finished
            if (this.state.suggestionsFor === this.state.value)
                return;

            this.setState({
                suggestionsFor: value,
                suggestions: predictions
            });

            this.fetchAutocompleteTimeoutId = undefined;
        }, 200);
    };

    handleSuggestionsClearRequested = () => {
        this.setState({
            suggestions: [],
        });
    };

    onValueChange = (event, {newValue}) => {
        this.setState({
            value: newValue,
        }, (() => {
            if (this.props.onChange)
                this.props.onChange({label: newValue});
        }).bind(this));
    };

    onBlur() {
        if (this.props.onBlur)
            this.props.onBlur();
    }

    handleSuggestionSelected = (event, {suggestion, suggestionValue, suggestionIndex, sectionIndex, method}) => {
        this.setState({
            value: suggestionValue,
            popper: '',
            suggestions: []
        }, (() => {
            if (this.props.onSelect)
                this.props.onSelect(suggestion);

            if (this.props.onChange)
                this.props.onChange(suggestion);
        }).bind(this));
    };

    onKeyDown(e) {
        if (e.keyCode === 13) { // Enter
            if (this.props.onEnter)
                this.props.onEnter(this.areSuggestionsShown);
        }
    }

    render() {
        const {classes} = this.props;

        const autosuggestProps = {
            renderInputComponent,
            highlightFirstSuggestion: true,
            suggestions: this.state.suggestions,
            onSuggestionsFetchRequested: this.handleSuggestionsFetchRequested.bind(this),
            onSuggestionsClearRequested: this.handleSuggestionsClearRequested.bind(this),
            onSuggestionSelected: this.handleSuggestionSelected.bind(this),
            getSuggestionValue,
            renderSuggestion,
        };

        return (
            <div className={classes.root}>
                <Autosuggest
                    {...autosuggestProps}
                    inputProps={{
                        classes,
                        label: this.props.label,
                        placeholder: this.props.placeholder,
                        value: this.state.value,
                        autoFocus: this.props.autoFocus || false,
                        onBlur: this.onBlur.bind(this),
                        onChange: this.onValueChange.bind(this),
                        onKeyDown: this.onKeyDown.bind(this)
                    }}
                    theme={{
                        container: classes.container,
                        suggestionsContainerOpen: classes.suggestionsContainerOpen,
                        suggestionsList: classes.suggestionsList,
                        suggestion: classes.suggestion,
                    }}
                    renderSuggestionsContainer={options => {
                        this.areSuggestionsShown = options.children !== null;

                        return (
                            <Paper {...options.containerProps} style={{
                                marginTop: 0,
                                overflow: "auto",
                                maxHeight: "150px"
                            }} square>
                                {options.children}
                            </Paper>
                        );
                    }}
                />
            </div>
        );
    }
}

GenericAutosuggest.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(GenericAutosuggest);